import type { ChecklistAction } from './actions';
import { ChecklistActionTypes } from './actionTypes';
import { type Draft, produce } from 'immer';
import type { Reducer } from 'redux';

import {
  type Checklist,
  type ChecklistsNames,
  type ChecklistsPage,
  isWebDataSuccess,
  type WebData,
  webDataError,
  webDataFetching,
  webDataNotAsked,
  webDataSuccess,
} from '@cobliteam/api-lib';

export interface ChecklistsState {
  templateChecklistsWebData: WebData<ChecklistsPage>;
  completedChecklistsWebData: WebData<ChecklistsPage>;
  checklistWebData: WebData<Checklist>;
  saveChecklistWebData: WebData<void>;
  deleteChecklistWebData: WebData<void>;
  completedChecklistsNamesWebData: WebData<ChecklistsNames>;
}

export const checklistsInitialState: ChecklistsState = {
  templateChecklistsWebData: webDataNotAsked(),
  completedChecklistsWebData: webDataNotAsked(),
  checklistWebData: webDataNotAsked(),
  saveChecklistWebData: webDataNotAsked(),
  deleteChecklistWebData: webDataNotAsked(),
  completedChecklistsNamesWebData: webDataNotAsked(),
};

export const checklistsReducer: Reducer<ChecklistsState, ChecklistAction> =
  produce((draft: Draft<ChecklistsState>, action: ChecklistAction) => {
    const getChecklistsWebData = (
      checklistsWebData: WebData<ChecklistsPage>,
      template: boolean
    ) => {
      if (template) {
        draft.templateChecklistsWebData = checklistsWebData;
        return;
      }

      draft.completedChecklistsWebData = checklistsWebData;
    };

    switch (action.type) {
      case ChecklistActionTypes.LOAD_CHECKLISTS_SUCCEEDED: {
        const { checklistsPage, template } = action.payload;

        getChecklistsWebData(webDataSuccess(checklistsPage), template);
        break;
      }

      case ChecklistActionTypes.LOAD_TEMPLATE_CHECKLISTS_REQUEST:
        draft.templateChecklistsWebData = webDataFetching();
        break;

      case ChecklistActionTypes.LOAD_COMPLETED_CHECKLISTS_REQUEST:
        draft.completedChecklistsWebData = webDataFetching();
        break;

      case ChecklistActionTypes.LOAD_CHECKLISTS_FAILED:
        getChecklistsWebData(webDataError(), action.payload);
        break;

      case ChecklistActionTypes.LOAD_CHECKLIST_REQUEST:
        draft.checklistWebData = webDataFetching();
        break;

      case ChecklistActionTypes.LOAD_CHECKLIST_SUCCEEDED:
        draft.checklistWebData = webDataSuccess(action.payload);
        break;

      case ChecklistActionTypes.LOAD_CHECKLIST_FAILED:
        draft.checklistWebData = webDataError();
        break;

      case ChecklistActionTypes.LOAD_COMPLETED_CHECKLISTS_NAMES_REQUEST:
        draft.completedChecklistsNamesWebData = webDataFetching();
        break;

      case ChecklistActionTypes.LOAD_COMPLETED_CHECKLISTS_NAMES_SUCCEEDED:
        draft.completedChecklistsNamesWebData = webDataSuccess(action.payload);
        break;

      case ChecklistActionTypes.LOAD_COMPLETED_CHECKLISTS_NAMES_FAILED:
        draft.completedChecklistsNamesWebData = webDataError();
        break;

      case ChecklistActionTypes.CREATE_CHECKLIST_REQUEST:
        draft.saveChecklistWebData = webDataFetching();
        break;

      case ChecklistActionTypes.CREATE_CHECKLIST_FAILED:
        draft.saveChecklistWebData = webDataError();
        break;

      case ChecklistActionTypes.CREATE_CHECKLIST_SUCCEEDED:
        if (isWebDataSuccess(draft.templateChecklistsWebData)) {
          const createdChecklist = action.payload;
          const updatedChecklists =
            draft.templateChecklistsWebData.data.page.content.concat(
              createdChecklist
            );
          const updatedChecklistsPage = {
            ...draft.templateChecklistsWebData.data,
            content: updatedChecklists,
          };

          draft.saveChecklistWebData = webDataSuccess(undefined);
          draft.templateChecklistsWebData = webDataSuccess(
            updatedChecklistsPage
          );
        }
        break;

      case ChecklistActionTypes.UPDATE_CHECKLIST_REQUEST:
        draft.saveChecklistWebData = webDataFetching();
        break;

      case ChecklistActionTypes.UPDATE_CHECKLIST_FAILED:
        draft.saveChecklistWebData = webDataError();
        break;

      case ChecklistActionTypes.UPDATE_CHECKLIST_SUCCEEDED: {
        const updatedChecklist = action.payload;
        const checklistsWebData = updatedChecklist.template
          ? draft.templateChecklistsWebData
          : draft.completedChecklistsWebData;

        if (isWebDataSuccess(checklistsWebData)) {
          const updatedChecklists = checklistsWebData.data.page.content.map(
            (checklist) => {
              if (checklist.id === updatedChecklist.id) {
                return updatedChecklist;
              }

              return checklist;
            }
          );

          const updatedChecklistsPage = {
            ...checklistsWebData.data,
            content: updatedChecklists,
          };

          draft.saveChecklistWebData = webDataSuccess(undefined);
          getChecklistsWebData(
            webDataSuccess(updatedChecklistsPage),
            updatedChecklist.template
          );
        }
        break;
      }

      case ChecklistActionTypes.DELETE_CHECKLIST_REQUEST:
        draft.deleteChecklistWebData = webDataFetching();
        break;

      case ChecklistActionTypes.DELETE_CHECKLIST_FAILED:
        draft.deleteChecklistWebData = webDataError();
        break;

      case ChecklistActionTypes.DELETE_CHECKLIST_SUCCEEDED: {
        const { deletedChecklistId, template } = action.payload;
        const checklistsWebData = template
          ? draft.templateChecklistsWebData
          : draft.completedChecklistsWebData;

        if (isWebDataSuccess(checklistsWebData)) {
          const updatedChecklists = checklistsWebData.data.page.content.filter(
            (checklist) => checklist.id !== deletedChecklistId
          );
          const updatedChecklistsPage = {
            ...checklistsWebData.data,
            content: updatedChecklists,
          };

          draft.deleteChecklistWebData = webDataSuccess(undefined);
          getChecklistsWebData(webDataSuccess(updatedChecklistsPage), template);
        }
        break;
      }
    }
  }, checklistsInitialState);

export default checklistsReducer;
